import { useCallback, useEffect, useState } from 'react'
import { UpsellFlow, CartTarget, AcceptConfirmation, buildFlow } from '../data/upsellFlows'
import { cartLink, variantIdOf, PRODUCT_LINKS } from '../data/productLinks'
import { trackCustom, goToShopifyCart } from '../utils/tracking'
import { flowSeen, markFlowSeen, setSelectionSource } from '../utils/funnelState'

export interface ActiveConfirmation extends AcceptConfirmation {
  target: CartTarget
}

/**
 * Runs one negotiation flow at a time. Accepting replaces the order with the
 * accepted product (never stacked); declining moves to the next step, and after
 * the last step sends the customer to exactly what they originally picked.
 * Each flow is marked as seen when it starts, so it never reopens this session.
 */
export function useUpsellFlow() {
  const [flow, setFlow] = useState<UpsellFlow | null>(null)
  const [index, setIndex] = useState(0)
  const [routing, setRouting] = useState(false)
  const [missingLink, setMissingLink] = useState(false)
  const [confirmation, setConfirmation] = useState<ActiveConfirmation | null>(null)

  const step = flow ? flow.steps[index] ?? null : null

  useEffect(() => {
    if (!step || !flow) return
    trackCustom('upsell_offer_viewed', { offer_id: step.id, flow: flow.kind, source: flow.source, total: step.upgrade.total })
    if (step.events) trackCustom(step.events.viewed, { total: step.upgrade.total, source: flow.source })
    if (step.alternate) {
      trackCustom('upsell_offer_viewed', { offer_id: `${step.id}-alternate`, flow: flow.kind, source: flow.source, total: step.alternate.summary.total })
      if (step.alternate.events) trackCustom(step.alternate.events.viewed, { total: step.alternate.summary.total, source: flow.source })
    }
  }, [step, flow])

  // Coming back from Shopify with the back button restores this page from
  // cache; reset so the popup is not stuck on "Taking you to checkout".
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRouting(false)
        setFlow(null)
        setConfirmation(null)
        setIndex(0)
      }
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  const route = useCallback((target: CartTarget, meta: Record<string, unknown> = {}) => {
    const href = cartLink(target.link, target.quantity)
    if (!href) {
      // Shopify product not connected yet: never send anyone to a broken cart.
      setMissingLink(true)
      console.warn(`[GritVue] PRODUCT_LINKS.${target.link} is empty. Paste its Shopify link in data/productLinks.ts.`)
      return
    }
    trackCustom('upsell_flow_completed', { final_product: target.link, value: target.value, quantity: target.quantity, ...meta })
    setRouting(true)
    goToShopifyCart(href, {
      content_name: target.name,
      content_ids: [variantIdOf(PRODUCT_LINKS[target.link])],
      quantity: target.quantity,
      value: Number(target.value.toFixed(2)),
    })
  }, [])

  const start = useCallback((next: UpsellFlow) => {
    markFlowSeen(next.kind)
    setSelectionSource(next.source)
    setRouting(false)
    setMissingLink(false)
    setConfirmation(null)
    setIndex(0)
    setFlow(next)
  }, [])

  const accept = useCallback(() => {
    if (!step || !flow) return
    trackCustom('upsell_offer_accepted', { offer_id: step.id, flow: flow.kind, source: flow.source, total: step.upgrade.total })
    if (step.events) trackCustom(step.events.accepted, { total: step.upgrade.total, source: flow.source })
    if (step.confirmOnAccept) {
      // Non-blocking confirmation: the next opportunity is only a link.
      setConfirmation({ ...step.confirmOnAccept, target: step.target })
      trackCustom('upsell_confirmation_viewed', { offer_id: step.id })
      setFlow(null)
      setIndex(0)
      return
    }
    route(step.target, { flow: flow.kind, source: flow.source, result: 'accepted', offer_id: step.id })
  }, [step, flow, route])

  /** The smaller offer on the same screen. Replaces the order, never stacks. */
  const acceptAlternate = useCallback(() => {
    if (!step?.alternate || !flow) return
    const alt = step.alternate
    trackCustom('upsell_offer_accepted', { offer_id: `${step.id}-alternate`, flow: flow.kind, source: flow.source, total: alt.summary.total })
    if (alt.events) trackCustom(alt.events.accepted, { total: alt.summary.total, source: flow.source })
    route(alt.target, { flow: flow.kind, source: flow.source, result: 'accepted_alternate', offer_id: `${step.id}-alternate` })
  }, [step, flow, route])

  const decline = useCallback(() => {
    if (!flow || !step) return
    trackCustom('upsell_offer_declined', { offer_id: step.id, flow: flow.kind, source: flow.source, total: step.current.total })
    if (step.events) trackCustom(step.events.declined, { total: step.current.total, source: flow.source })
    setMissingLink(false)
    if (index < flow.steps.length - 1) {
      setIndex(index + 1)
      return
    }
    route(flow.fallback, { flow: flow.kind, source: flow.source, result: 'declined_all' })
  }, [flow, step, index, route])

  const continueToCheckout = useCallback(() => {
    if (!confirmation) return
    route(confirmation.target, { result: 'accepted_confirmed' })
  }, [confirmation, route])

  const followUp = useCallback(() => {
    if (!confirmation?.followUp) return
    const { flow: kind, source } = confirmation.followUp
    trackCustom('crew_pricing_link_clicked', { flow: kind, source })
    if (flowSeen(kind)) {
      route(confirmation.target, { result: 'accepted_confirmed' })
      return
    }
    start(buildFlow(kind, source))
  }, [confirmation, route, start])

  // Closing keeps the customer's selection on the card and stays on the page.
  const close = useCallback(() => {
    if (routing) return
    setFlow(null)
    setConfirmation(null)
    setIndex(0)
    setMissingLink(false)
  }, [routing])

  return { step, confirmation, routing, missingLink, start, accept, acceptAlternate, decline, continueToCheckout, followUp, close }
}
